
import React, { useState, useEffect } from "react";
import { DataToolbar, ActionButtons } from "../components/DataToolbar";
import { Modal } from "../components/Modal";
import { FileUploader } from "../components/FileUploader";
import { exportToExcel } from "../export/excel";
import { useToast } from "../components/Toast";
import { useAiContext } from "../ai/AiContext";
import { generateDocCode } from "../utils/idGenerator";
import { DateField } from "../components/form/DateField";
import { formatDate, parseDate } from "../utils/dateUtils";

import { db, serverTimestamp } from "../../firebaseClient";
import { uploadIpMatterFiles } from "../services/storageUpload";
import { createLegalActivity } from "../services/legalActivityService";
import { useAuth } from "../context/AuthContext";
import { SignRequestButton } from "../modules/sign/components/SignRequestButton";
import { canEditModule } from "../config/roles";

import type { IpMatter, IpType, IpMatterKind, IpMatterStatus, IpProgressLog } from "../types/ipMatter";
import type { AttachmentMeta } from "../types/attachments";
import { logLegalActivityOnChange } from "../services/legalActivityLogger";
import { completeTaskBySource } from "../services/taskService";

const IP_TYPES: IpType[] = [
  "商標", "著作權", "專利-發明", "專利-新型", "專利-設計", "域名", "商業秘密", "其他"
];

const STATUS_OPTIONS: (IpMatterStatus | "申請中")[] = [
  "申請中", "初步蒐證中", "評估中", "草擬 / 準備送件", "已送件 / 審查中", "要求補正中", 
  "已核准 / 登錄成功", "駁回 / 不核准", "協商中", "已完成下架 / 停止侵權", 
  "訴訟中", "已結案", "失效 / 未續展"
];

export function IpMattersPage() {
  const { user } = useAuth();
  const canEdit = user ? canEditModule(user.role, 'IP') : false;

  const [items, setItems] = useState<IpMatter[]>([]);
  const [filteredItems, setFilteredItems] = useState<IpMatter[]>([]);
  const [error, setError] = useState<Error | null>(null);
  
  const [search, setSearch] = useState("");
  const [filterKind, setFilterKind] = useState<"ALL" | IpMatterKind>("ALL");
  const [filterType, setFilterType] = useState<"ALL" | string>("ALL");
  const [filterStatus, setFilterStatus] = useState<"ALL" | string>("ALL");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<IpMatter | null>(null);
  const [previewItem, setPreviewItem] = useState<IpMatter | null>(null);

  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File|null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const { requestDelete, setActiveItem } = useAiContext();
  const { showToast } = useToast();

  useEffect(() => {
    const unsubscribe = db.collection("ip_matters").orderBy("createdAt", "desc").onSnapshot(
      (snapshot) => {
        const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as IpMatter));
        setItems(list);
        setError(null);
      },
      (err) => {
        console.error("IP Matters listener error:", err);
        setError(err);
      }
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let res = items;
    if (search) {
      const s = search.toLowerCase();
      res = res.filter(i => i.title.toLowerCase().includes(s) || i.ipNo.toLowerCase().includes(s) || i.brandOrWorkName?.toLowerCase().includes(s));
    }
    if (filterKind !== "ALL") res = res.filter(i => i.matterType === filterKind);
    if (filterType !== "ALL") res = res.filter(i => i.ipType === filterType);
    if (filterStatus !== "ALL") res = res.filter(i => i.status === filterStatus);
    setFilteredItems(res);
  }, [items, search, filterKind, filterType, filterStatus]);

  useEffect(() => {
    if (editingItem || previewItem) {
        const target = editingItem || previewItem;
        setActiveItem({
            type: "other", 
            id: target!.ipNo,
            title: target!.title,
            data: target,
            attachments: target!.attachments
        });
    } else {
        setActiveItem(null);
    }
  }, [editingItem, previewItem, setActiveItem]);

  const handleDelete = (id: string, title: string) => {
    requestDelete({
      targetType: 'ipMatter',
      targetId: id,
      summary: title,
      onConfirm: async () => {
        try {
            await db.collection("ip_matters").doc(id).delete();
            await completeTaskBySource(id); // Clean up tasks
            await logLegalActivityOnChange({
                module: "智財權",
                docId: id,
                title: title,
                action: "刪除",
                userEmail: user?.email || "unknown"
            });
            showToast("已刪除 IP 資料");
        } catch(e) { console.error(e); showToast("刪除失敗", "error"); }
      }
    });
  };

  const handleExport = () => {
    const data = filteredItems.map(i => ({
        ...i,
        logs: i.progressLogs?.map(l => `[${l.at.slice(0,10)}] ${l.note}`).join(" | "),
        attachments: i.attachments?.map(a => a.name).join(", ")
    }));
    exportToExcel("IpMatters", data);
  };

  const handleImportSubmit = async () => {
    if (!importFile) return;
    setIsImporting(true);
    try {
        const text = await importFile.text();
        const lines = text.split(/\r?\n/);
        const batch = db.batch();
        let count = 0;
        
        for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if(!line) continue;
            const cols = line.split(",");
            if(cols.length < 2) continue;
            
            const docRef = db.collection("ip_matters").doc();
            
            const no = cols[3] || generateDocCode("ip", undefined, items);

            batch.set(docRef, {
                title: cols[0],
                ipType: cols[1] || '商標',
                status: cols[2] || '申請中',
                ipNo: no,
                matterType: '申請',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
            count++;
        }
        await batch.commit();
        showToast(`匯入成功 ${count} 筆`);
        setIsImportOpen(false);
        setImportFile(null);
    } catch(e) {
        console.error(e);
        showToast("匯入失敗", "error");
    } finally {
        setIsImporting(false);
    }
  };

  const handleOpenCreate = () => {
      setEditingItem(null); 
      setIsModalOpen(true);
  };

  return (
    <div>
      <div className="section" style={{ marginBottom: 16, padding: "12px 16px" }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <input className="input" style={{ width: 220 }} placeholder="搜尋編號、標題..." value={search} onChange={e => setSearch(e.target.value)} />
          <select className="input" style={{ width: 100 }} value={filterKind} onChange={e => setFilterKind(e.target.value as any)}>
             <option value="ALL">全部種類</option>
             <option value="申請">申請</option>
             <option value="維權">維權</option>
          </select>
          <select className="input" style={{ width: 120 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
             <option value="ALL">所有IP類型</option>
             {IP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select className="input" style={{ width: 150 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
             <option value="ALL">所有狀態</option>
             {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <DataToolbar 
        canEdit={canEdit} 
        onCreate={handleOpenCreate} 
        onExport={handleExport}
        onImport={() => setIsImportOpen(true)} 
      />

      {error && <div className="err" style={{marginBottom:16}}>讀取失敗: {error.message}</div>}

      <div style={{ display: "grid", gap: 10 }}>
        {filteredItems.map(item => (
          <div key={item.id} className="tile">
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
               <div style={{ fontSize: 13, color: "var(--muted)" }}>
                  <span className="badge" style={{ marginRight: 8, background: item.matterType === "申請" ? "#10b981" : "#ef4444", color: "#fff", border: "none" }}>{item.matterType}</span>
                  {item.ipNo}
               </div>
               <div className="badge" style={{ color: item.status.includes("結案") ? "var(--muted)" : "var(--ok)", borderColor: "currentColor" }}>{item.status}</div>
            </div>
            <div style={{ fontSize: 16, fontWeight: "bold", color: "#fff", marginBottom: 4 }}>
                {item.title}
            </div>
            <div className="grid" style={{ gap: 8, gridTemplateColumns: "1fr auto" }}>
               <div style={{ fontSize: 13, color: "var(--muted)" }}>
                  <div>類型: {item.ipType} | 地區: {item.region} | 權利人: {item.rightsOwner}</div>
                  <div>部門: {item.department || "-"}</div>
                  {item.nextImportantDate && <div style={{ color: "#fbbf24", marginTop: 4 }}>下次重要: {item.nextImportantDate}</div>}
               </div>
               <div style={{ alignSelf: "flex-end", textAlign: "right" }}>
                  <div style={{display:'flex', gap:8, justifyContent:'flex-end', marginBottom: 8}}>
                     <SignRequestButton 
                        sourceType="ip" 
                        sourceId={item.id} 
                        sourceTitle={item.title} 
                        defaultCategory="智財申請" 
                     />
                  </div>
                  <ActionButtons 
                    canEdit={canEdit} 
                    onPreview={() => setPreviewItem(item)} 
                    onEdit={() => { setEditingItem(item); setIsModalOpen(true); }} 
                    onDelete={() => handleDelete(item.id, item.title)} 
                  />
               </div>
            </div>
          </div>
        ))}
        {filteredItems.length === 0 && <div className="p" style={{textAlign:"center", padding:20}}>無符合資料</div>}
      </div>

      {previewItem && <IpMatterPreviewModal data={previewItem} onClose={() => setPreviewItem(null)} />}
      
      {isModalOpen && (
        <IpMatterFormModal 
          initialData={editingItem} 
          existingItems={items}
          onClose={() => setIsModalOpen(false)} 
          onSuccess={() => { showToast(editingItem ? "已更新" : "已新增"); setIsModalOpen(false); }} 
        />
      )}

      {isImportOpen && (
        <Modal title="匯入 IP 資料" onClose={() => setIsImportOpen(false)} width={500}>
           <div className="p">請上傳 CSV 檔案 (格式: 標題,IP類型,狀態,編號)</div>
           <input type="file" accept=".csv" onChange={e => setImportFile(e.target.files?.[0] || null)} style={{margin: '20px 0'}} />
           <div className="row" style={{justifyContent: 'flex-end'}}>
              <button className="btn" onClick={handleImportSubmit} disabled={!importFile || isImporting}>{isImporting?"匯入中...":"匯入"}</button>
           </div>
        </Modal>
      )}
    </div>
  );
}

function IpMatterFormModal({ initialData, existingItems, onClose, onSuccess }: { initialData: IpMatter | null, existingItems: IpMatter[], onClose: () => void, onSuccess: () => void }) {
  // ... existing form ...
  const [form, setForm] = useState<Partial<IpMatter>>(initialData || { 
    matterType: "申請", ipType: "商標", status: "申請中" as any, attachments: [], progressLogs: [] 
  });
  const [newProgressNote, setNewProgressNote] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setDraft } = useAiContext();
  const { user } = useAuth();

  const handleChange = (k: keyof IpMatter, v: any) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!initialData && !form.ipNo) {
        const no = generateDocCode("ip", undefined, existingItems);
        handleChange("ipNo", no);
    }
  }, [initialData]);

  useEffect(() => {
    setDraft({ 
        type: 'other', 
        data: form,
        onApplyTitle: (v) => setForm(p => ({...p, title: v})),
        onApplyNotes: (v) => setForm(p => ({...p, summary: (p.summary||"") + "\n" + v}))
    });
    return () => setDraft(null);
  }, [form, setDraft]);

  const handleRegenerateNo = () => {
    const no = generateDocCode("ip", undefined, existingItems);
    handleChange("ipNo", no);
  };

  const handleSave = async () => {
    if (!form.title) { alert("請填寫標題"); return; }
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
        const nowIso = new Date().toISOString();
        const payload = { ...form };
        if (!payload.attachments) payload.attachments = [];
        const existingAttachments = payload.attachments as AttachmentMeta[];

        if (newProgressNote.trim()) {
            const log: IpProgressLog = {
                at: nowIso,
                note: newProgressNote.trim(),
                author: "User"
            };
            payload.progressLogs = [...(payload.progressLogs || []), log];
            payload.currentProgress = log.note;
        }

        let docId = payload.id;
        const action = docId ? "修改" : "新增";
        
        if (initialData && docId) {
            delete payload.id;
            await db.collection("ip_matters").doc(docId).update({ ...payload, updatedAt: serverTimestamp() });
        } else {
            delete payload.id;
            if (!payload.ipNo) {
               payload.ipNo = generateDocCode("ip", undefined, existingItems);
            }
            const ref = await db.collection("ip_matters").add({ ...payload, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
            docId = ref.id;
        }

        if (pendingFiles.length > 0 && docId) {
            const uploaded = await uploadIpMatterFiles(docId, pendingFiles);
            const finalAttachments = [...existingAttachments, ...uploaded];
            await db.collection("ip_matters").doc(docId).update({ attachments: finalAttachments });
        }

        // Auto-close tasks if status is terminal
        if (docId && (payload.status === "已核准 / 登錄成功" || payload.status === "已結案" || payload.status === "駁回 / 不核准")) {
            await completeTaskBySource(docId);
        }

        await logLegalActivityOnChange({
            module: "智財權",
            docId: docId || "unknown",
            title: payload.title || "",
            action: action,
            userEmail: user?.email || "unknown"
        });

        onSuccess();
    } catch(e) {
        console.error(e);
        alert("儲存失敗");
    } finally {
        setIsSubmitting(false);
    }
  };

  return (
    <Modal title={initialData ? "編輯IP資料" : "新增智財案件"} onClose={onClose} width={800}>
      <div className="grid">
        <div className="field"><label className="label">種類</label>
           <select className="input" value={form.matterType} onChange={e => handleChange("matterType", e.target.value)}>
             <option value="申請">申請</option>
             <option value="維權">維權</option>
           </select>
        </div>
        <div className="field"><label className="label">IP類型</label>
           <select className="input" value={form.ipType} onChange={e => handleChange("ipType", e.target.value)}>
             {IP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
           </select>
        </div>
        <div className="field"><label className="label">編號 (系統自動)</label>
           <div style={{display:"flex", gap:8}}>
             <input className="input" value={form.ipNo||""} readOnly style={{background: 'rgba(255,255,255,0.05)', color: '#fbbf24', fontWeight:'bold', border:'1px dashed #555'}} />
             {!initialData && (
                 <button className="smallbtn" style={{width:"auto"}} onClick={handleRegenerateNo} title="重新產生">↻</button>
             )}
           </div>
        </div>
        <div className="field"><label className="label">標題 (必填)</label><input className="input" value={form.title||""} onChange={e => handleChange("title", e.target.value)} /></div>
        <div className="field"><label className="label">品牌/作品</label><input className="input" value={form.brandOrWorkName||""} onChange={e => handleChange("brandOrWorkName", e.target.value)} /></div>
        <div className="field"><label className="label">類別(Class)</label><input className="input" value={form.classes||""} onChange={e => handleChange("classes", e.target.value)} /></div>
        <div className="field"><label className="label">地區</label><input className="input" value={form.region||""} onChange={e => handleChange("region", e.target.value)} /></div>
        <div className="field"><label className="label">權利人/申請人</label><input className="input" value={form.rightsOwner||""} onChange={e => handleChange("rightsOwner", e.target.value)} /></div>
        <div className="field"><label className="label">負責部門</label><input className="input" value={form.department||""} onChange={e => handleChange("department", e.target.value)} /></div>
        <div className="field"><label className="label">代理事務所</label><input className="input" value={form.agentFirm||""} onChange={e => handleChange("agentFirm", e.target.value)} /></div>
        <div className="field"><label className="label">狀態</label>
           <select className="input" value={form.status} onChange={e => handleChange("status", e.target.value)}>
             {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
           </select>
        </div>

        {form.matterType === "申請" && (
           <>
             <DateField label="申請日" value={parseDate(form.filingDate||null)} onChange={d => handleChange("filingDate", formatDate(d))} />
             <DateField label="核准/登錄日" value={parseDate(form.grantDate||null)} onChange={d => handleChange("grantDate", formatDate(d))} />
             <div className="field"><label className="label">註冊號</label><input className="input" value={form.registrationNo||""} onChange={e => handleChange("registrationNo", e.target.value)} /></div>
             <DateField label="專用期限" value={parseDate(form.expiryDate||null)} onChange={d => handleChange("expiryDate", formatDate(d))} />
           </>
        )}

        {form.matterType === "維權" && (
           <>
             <div className="field"><label className="label">侵權型態</label><input className="input" value={form.infringementType||""} onChange={e => handleChange("infringementType", e.target.value)} /></div>
             <DateField label="發現日期" value={parseDate(form.discoveryDate||null)} onChange={d => handleChange("discoveryDate", formatDate(d))} />
             <div className="field"><label className="label">維權途徑</label><input className="input" value={form.enforcementChannel||""} onChange={e => handleChange("enforcementChannel", e.target.value)} /></div>
           </>
        )}
        
        <DateField label="下次重要日期" value={parseDate(form.nextImportantDate||null)} onChange={d => handleChange("nextImportantDate", formatDate(d))} />
        
        <div className="field" style={{ gridColumn: "1/-1" }}><label className="label">摘要說明</label>
           <textarea className="input" rows={2} value={form.summary||""} onChange={e => handleChange("summary", e.target.value)} />
        </div>

        <div className="field" style={{gridColumn:"1/-1", background:"rgba(255,255,255,0.03)", padding:12, borderRadius:8}}>
           <label className="label" style={{color:"#fbbf24"}}>+ 新增進度說明 (寫入歷程)</label>
           <textarea className="input" rows={2} placeholder="輸入最新進度..." value={newProgressNote} onChange={e => setNewProgressNote(e.target.value)} />
        </div>
      </div>

      <FileUploader 
        files={form.attachments || []} 
        onChange={files => setForm(p => ({ ...p, attachments: files }))} 
        onFileSelect={(fs) => setPendingFiles(prev => [...prev, ...fs])}
      />
      <div className="row" style={{ marginTop: 20, justifyContent: "flex-end" }}>
        <button className="btn" style={{ width: "auto" }} disabled={isSubmitting} onClick={handleSave}>
          {isSubmitting ? "儲存中..." : "儲存 / 建立"}
        </button>
      </div>
    </Modal>
  );
}

function IpMatterPreviewModal({ data, onClose }: { data: IpMatter, onClose: () => void }) {
  return (
    <Modal title={`IP預覽: ${data.ipNo}`} onClose={onClose} width={700}>
       <div className="grid">
          <div className="field"><label className="label">種類</label><div>{data.matterType} / {data.ipType}</div></div>
          <div className="field"><label className="label">標題</label><div>{data.title}</div></div>
          <div className="field"><label className="label">品牌/作品</label><div>{data.brandOrWorkName}</div></div>
          <div className="field"><label className="label">地區</label><div>{data.region}</div></div>
          <div className="field"><label className="label">負責部門</label><div>{data.department || "-"}</div></div>
          <div className="field"><label className="label">狀態</label><div className="badge" style={{display:"inline-block"}}>{data.status}</div></div>
          <div className="p" style={{gridColumn:"1/-1", marginTop: 8}}>摘要: {data.summary || "(無)"}</div>
       </div>
       <div style={{marginTop:20}}><FileUploader files={data.attachments||[]} readOnly onChange={()=>{}} /></div>
    </Modal>
  );
}
